import { gql } from "~/__generated__/graphql";
import { data, LoaderFunctionArgs, MetaFunction, useLoaderData } from "react-router";
import { useState } from "react";
import { FiCalendar, FiMapPin } from "react-icons/fi";
import { createApolloClient } from "~/.server/apollo";
import { ClubNotFound } from "~/components/clubs/ClubNotFound";
import { CTADialog } from "~/components/CTADialog";
import GreenFlowerImage from "~/images/green-flower.png";
import RedFlowerImage from "~/images/red-flower.png";
import { buildAppsFlyerLink, getUTMParamsWithSession } from "~/.server/appsflyer";
import { commitSession } from "~/.server/session";
import { Text } from "~/components/Text";
import { SectionHeading } from "~/components/SectionHeading";
import { ImageCarousel } from "~/components/ImageCarousel";
import { GoldHostBadge } from "~/components/GoldHostBadge";

import { useRootLocale } from "~/root";
import { getRegionalCopy, type SupportedLocale } from "~/utils/localization";

import { ClubHeader } from "./ClubHeader";
import { JoinClubCard } from "./JoinClubCard";
import { GroupChatsList } from "./GroupChatsList";
import { HostsCards } from "./HostsCards";
import { UpcomingEventCard } from "./UpcomingEventCard";
import { EventMomentsCarousel } from "./EventMomentsCarousel";
import { InsightsList } from "./InsightsList";
import { Faqs } from "./Faqs";
import { UpdatesTeaser } from "./UpdatesTeaser";
import { BottomCTA } from "./BottomCTA";

export const GET_CLUB_QUERY = gql(`
  query GetClub($params: ClubParams!) {
    club(params: $params) {
      id
      name
      headline
      description
      imageUrl
      profileTitle
      profileHeadline
      profileDescription
      profileImageUrl
      premium
      foundingMember
      verified
      locationSuburb {
        suburb
      }
      locationCities {
        city
      }
      meetingDays
      managers {
        id
        primary
        displayHost
        profileName
        profileHeadline
        profileDescription
        profileImageUrl
        user {
          id
          firstName
          profileImageUrl
        }
      }
      members(first: 10) {
        pageInfo {
          totalSize
        }
        edges {
          cursor
          node {
            user {
              id
              firstName
              name
              profileImageUrl
            }
          }
        }
      }
      events(params: { upcoming: true, cancelled: false }, first: 10) {
        pageInfo {
          totalSize
        }
        edges {
          cursor
          node {
            id
            title
            startDate
            bannerImageUrl
            club {
              id
              name
            }
            location: place {
              suburb
              city
              state
            }
            hosts {
              primary
              user {
                id
                name
                profileImageUrl
              }
            }
          }
        }
      }
      plansHosted
      chatRoomsV2(params: { type: CLUB }, first: 6) {
        edges {
          cursor
          node {
            id
            name
            description
            imageUrl
            memberCount
          }
        }
      }
      eventMoments {
        id
        imageUrl
        caption
      }
      insights {
        id
        answer
        visible
      }
      faqs {
        id
        label
        answer
      }
      socials {
        id
        type
        url
        handle
      }
    }
  }
`);

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const clubId = params.clubId;

  const url = new URL(request.url);
  const type = url.searchParams.get("type") as
    | "club-invite"
    | "club-chat-invite"
    | null;
  const chatId = url.searchParams.get("chatId");
  const { utmParams, session, needsCommit } = await getUTMParamsWithSession(request);

  const headers: HeadersInit = {};
  if (needsCommit) {
    headers["Set-Cookie"] = await commitSession(session);
  }

  if (!clubId) {
    return data(
      {
        club: null,
        type: type,
        openAppLink: "",
        error: "Club id is missing",
      },
      { status: 500, headers },
    );
  }

  if (type === "club-chat-invite" && !chatId) {
    return data(
      {
        club: null,
        type: type,
        openAppLink: "",
        error: "Chat id is missing",
      },
      { status: 500, headers },
    );
  }

  const openAppLink = buildAppsFlyerLink(
    type === "club-invite"
      ? { type: "club-invite", clubId }
      : type === "club-chat-invite"
        ? { type: "club-chat-invite", clubId, chatId: chatId! }
        : { type: "club", clubId },
    utmParams,
  );

  try {
    const { client } = await createApolloClient(request);

    const { data: result, error } = await client.query({
      query: GET_CLUB_QUERY,
      variables: {
        params: { id: clubId },
      },
    });
    if (error || !result) {
      return data(
        {
          club: null,
          openAppLink,
          type: type,
          error: "An unknown error occurred. Please try again later.",
        },
        { status: 500, headers },
      );
    }

    return data(
      {
        club: result.club,
        type: type,
        openAppLink,
        error: null,
      },
      { headers },
    );
  } catch (error) {
    console.error("Error fetching club:", error);

    return data(
      {
        club: null,
        openAppLink,
        type: type,
        error: "Error occurred loading the club",
      },
      { status: 500, headers },
    );
  }
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data?.error || !data?.club) {
    return [
      { title: "Club not found | Butter" },
      {
        name: "description",
        content: "The club could not be found.",
      },
    ];
  }

  const description =
    data.club.headline?.substring(0, 160) || `Join ${data.club.name} on Butter`;
  const url = `https://app.joinbutter.co/clubs/${data.club.id}`;

  return [
    { title: `${data.club.name} | Butter` },
    { name: "description", content: description },
    { property: "og:title", content: data.club.name },
    { property: "og:description", content: description },
    { property: "og:image", content: data.club.imageUrl },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    { property: "og:site_name", content: "Butter" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: data.club.name },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: data.club.imageUrl },
    { tagName: "link", rel: "canonical", href: url },
  ];
};

type AppModalVariant = null | "join" | "saved" | "chat" | "updates" | "members";

function getClubModalCopy(
  variant: AppModalVariant,
  locale: SupportedLocale,
) {
  const regionalCopy = getRegionalCopy(locale);

  switch (variant) {
    case "saved":
      return {
        title: `Your ${regionalCopy.place} is saved`,
        description:
          `Confirm your ${regionalCopy.place}, see who else is going, and join the group chat in the app.`,
        qrTitle: `Your ${regionalCopy.place} is saved`,
        iconUrl: GreenFlowerImage,
        buttonLabel: "Get the app to join",
      };
    case "chat":
      return {
        title: "Private chats",
        description: "Only members can join the club group chats.",
        qrTitle: "Only members can join the club chats",
        iconUrl: RedFlowerImage,
        buttonLabel: "Get the app to join",
      };
    case "updates":
      return {
        title: "Private updates",
        description: "Only members can see and reply to the club's updates.",
        qrTitle: "Only members can see club updates",
        iconUrl: RedFlowerImage,
        buttonLabel: "Get the app to join",
      };
    case "members":
      return {
        title: "Private member list",
        description:
          "Only members can see who else is here, and start conversations.",
        qrTitle: "Only members can see who else is here",
        iconUrl: RedFlowerImage,
        buttonLabel: "Get the app to join",
      };
    case "join":
    default:
      return {
        title: "The club doesn't stop between events",
        description:
          "The app is where the club actually lives — group chats, DMs with members, and plans before they go public.",
        iconUrl: GreenFlowerImage,
        buttonLabel: "Download Butter for free",
      };
  }
}

export default function ClubPage() {
  const locale = useRootLocale();
  const { club, openAppLink, type } = useLoaderData<typeof loader>();
  const [appModalVariant, setAppModalVariant] = useState<AppModalVariant>(
    type === "club-invite"
      ? "saved"
      : type === "club-chat-invite"
        ? "chat"
        : null,
  );

  const memberCount = club?.members.pageInfo.totalSize ?? 0;
  const pastEventsCount = club?.plansHosted ?? 0;
  const meetingRegularly = (club?.meetingDays.length ?? 0) > 0;
  const groupChats = club?.chatRoomsV2.edges ?? [];


  if (!club) {
    return (
      <main className="max-w-[1000px] w-full text-butter-green pb-10 lg:pb-0">
        <ClubNotFound />
      </main>
    );
  }

  const visibleHosts = club.managers.filter((m) => m.displayHost);
  const primaryManager =
    visibleHosts.find((m) => m.primary) ?? visibleHosts[0];
  const primaryHostName =
    primaryManager?.profileName ??
    primaryManager?.user.firstName ??
    club.profileTitle ??
    undefined;
  const otherHostCount = Math.max(visibleHosts.length - 1, 0);
  const locationLabel = club.locationCities.map((c) => c.city).join(", ");

  const openJoinModal = () => setAppModalVariant("join");
  const openUpdatesModal = () => setAppModalVariant("updates");
  const openChatModal = () => setAppModalVariant("chat");
  const openMembersModal = () => setAppModalVariant("members");

  const joinCard = (
    <JoinClubCard
      members={club.members.edges}
      totalSize={memberCount}
      onJoin={openJoinModal}
      onMembers={openMembersModal}
    />
  );

  const groupChatsSection = groupChats.length > 0 && (
    <GroupChatsList chats={groupChats} onSelect={openChatModal} />
  );

  return (
    <main className="max-w-[1000px] w-full text-butter-green pb-10 lg:pb-0">
      {/* Mobile-only: full-width banner at the very top */}
      <div className="relative mb-3 lg:hidden">
        <ImageCarousel imageUrls={[club.imageUrl]} alt={club.name} />
        {(club.premium || club.foundingMember) && (
          <GoldHostBadge
            className="absolute top-3 left-3 z-10"
            label={club.premium ? "Gold host" : "Founding member"}
          />
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start lg:gap-9">
        {/* Desktop-only sidebar */}
        <aside className="hidden lg:flex lg:w-[360px] lg:flex-shrink-0 lg:sticky lg:top-[101px] flex-col gap-7">
          <div className="relative">
            <ImageCarousel imageUrls={[club.imageUrl]} alt={club.name} />
            {(club.premium || club.foundingMember) && (
              <GoldHostBadge
                className="absolute top-3 left-3 z-10"
                label={club.premium ? "Gold host" : "Founding member"}
              />
            )}
          </div>
          {joinCard}
          {groupChatsSection}
        </aside>

        {/* Right column — main content */}
        <div className="butter-reveal flex-1 min-w-0 mt-2 lg:mt-0 lg:max-w-[600px] flex flex-col gap-11">
          <ClubHeader
            name={club.name}
            hostName={primaryHostName}
            otherHostCount={otherHostCount}
            premium={club.premium}
            onSelectHost={openJoinModal}
          />

          <div className="-mt-6 flex flex-wrap items-start gap-x-5 gap-y-3">
            {locationLabel && (
              <span className="inline-flex items-center gap-2.5">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-butter-cream/20 from-[25%] to-butter-cream">
                  <FiMapPin size={22} className="text-butter-green" aria-hidden />
                </span>
                <span className="flex flex-col leading-tight">
                  <Text as="span" variant="secondary" size="md" weight="bold">
                    {locationLabel}
                  </Text>
                  {meetingRegularly && (
                    <Text as="span" variant="secondary" size="sm">
                      Meeting regularly
                    </Text>
                  )}
                </span>
              </span>
            )}
            {pastEventsCount > 0 && (
              <span className="inline-flex items-center gap-2.5">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-butter-cream/20 from-[25%] to-butter-cream">
                  <FiCalendar
                    size={22}
                    className="text-butter-green"
                    aria-hidden
                  />
                </span>
                <span className="flex flex-col leading-tight">
                  <Text as="span" variant="secondary" size="md" weight="bold">
                    {pastEventsCount === 1
                      ? "1 plan"
                      : `${pastEventsCount} plans`}
                  </Text>
                  <Text as="span" variant="secondary" size="sm">
                    hosted so far
                  </Text>
                </span>
              </span>
            )}
          </div>

          {(club.headline || club.description) && (
            <section className="flex flex-col gap-3">
              {club.headline && (
                <Text
                  variant="secondary"
                  size="md"
                  weight="bold"
                  className="leading-snug"
                >
                  {club.headline.trim()}
                </Text>
              )}
              {club.description && (
                <Text
                  variant="secondary"
                  size="md"
                  className="whitespace-pre-line break-words leading-relaxed"
                >
                  {club.description.trim()}
                </Text>
              )}
            </section>
          )}

          {/* Mobile-only: join card sits high up so the CTA is reachable */}
          <div className="lg:hidden">{joinCard}</div>

          {club.eventMoments.length > 0 && (
            <section className="flex flex-col gap-4">
              <SectionHeading as="h2">Recent events</SectionHeading>
              <EventMomentsCarousel moments={club.eventMoments} />
            </section>
          )}

          {club.insights.some((i) => i.visible && i.answer.trim()) && (
            <section className="flex flex-col gap-3">
              <SectionHeading as="h2">
                We&apos;re perfect for you if...
              </SectionHeading>
              <InsightsList insights={club.insights} />
            </section>
          )}

          {club.events.edges.length > 0 && (
            <section className="flex flex-col gap-4">
              <SectionHeading as="h2">What&apos;s coming up</SectionHeading>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {club.events.edges.map((edge) => (
                  <UpcomingEventCard key={edge.node.id} event={edge.node} />
                ))}
              </div>
            </section>
          )}

          {visibleHosts.length > 0 && (
            <section className="flex flex-col gap-4">
              <SectionHeading as="h2">
                {visibleHosts.length === 1 ? "Meet the host" : "Meet the hosts"}
              </SectionHeading>
              <HostsCards
                managers={club.managers}
                clubLocation={locationLabel}
                onSelect={openJoinModal}
              />
            </section>
          )}

          {club.faqs.length > 0 && (
            <section className="flex flex-col gap-3">
              <SectionHeading as="h2">FAQs</SectionHeading>
              <Faqs faqs={club.faqs} />
            </section>
          )}

          <UpdatesTeaser
            members={club.members.edges}
            onJoin={openUpdatesModal}
          />

          {/* Mobile-only: group chats appear at the end */}
          {groupChatsSection && (
            <div className="lg:hidden">{groupChatsSection}</div>
          )}
        </div>
      </div>

      <BottomCTA clubName={club.name} onJoin={openJoinModal} />

      <CTADialog
        open={appModalVariant !== null}
        onClose={() => setAppModalVariant(null)}
        link={openAppLink}
        {...getClubModalCopy(appModalVariant, locale)}
        tracking={{
          direction: type === "club-chat-invite" ? "club_chats" : "clubs",
          surface: `club_${appModalVariant ?? "unknown"}_modal`,
          appsflyerType: type ?? "club",
        }}
      />
    </main>
  );
}
